import { useCallback, useSyncExternalStore } from 'react';
import { Status, } from './utils.types';
const zustandAbortWrapper = (handler, setWithMore, get) => (payload) => {
    const ac = new AbortController();
    const promise = handler(payload, {
        signal: ac.signal,
        setState: setWithMore(payload),
        getState: get,
    });
    if (promise instanceof Promise)
        promise.abort = ac.abort.bind(ac);
    return promise;
};
export const abortController = (store, set, get, name) => Object.entries(store instanceof Object ? store : {}).map(([key, handler]) => {
    if (typeof handler === 'function') {
        return [
            key,
            zustandAbortWrapper(handler, (payload) => ((data, more, rewrite = false) => (set(data, rewrite, { payload, type: `${name}/${key}${more ? `/${more}` : ''}` }))), get),
        ];
    }
    return [key, handler];
}).reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
const store = (() => {
    let state = new Map();
    const listeners = [];
    const dispatch = () => listeners.forEach((listener) => listener());
    return {
        getState: () => state,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setState: (key, setter) => {
            const data = state.get(key);
            const nextState = setter(data);
            state.set(key, { ...data, ...nextState });
            state = new Map(state);
            setTimeout(dispatch, 0);
        },
        subscribe: (listener) => {
            listeners.push(listener);
            return () => listeners.splice(listeners.indexOf(listener), 1);
        },
    };
})();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useStatus = (fn, idleTimeout) => {
    useSyncExternalStore(store.subscribe, store.getState);
    if (!store.getState().get(fn))
        store.setState(fn, () => ({ status: Status.Idle }));
    const currentData = store.getState().get(fn);
    const func = useCallback((payload) => {
        store.setState(fn, () => ({ func, status: Status.Pending })); // eslint-disable-line
        const next = fn(payload);
        next
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((res) => {
            store.setState(fn, () => ({ data: res, status: Status.Fulfilled, error: undefined }));
            if (idleTimeout) {
                setTimeout(() => {
                    store.setState(fn, () => ({
                        data: undefined,
                        status: Status.Idle,
                        error: undefined,
                        func: undefined,
                    }));
                }, idleTimeout);
            }
        })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((err) => {
            if (err === undefined)
                return; // Кейс когда фактически ошибки запроса нет, просто запрос кенселится
            store.setState(fn, () => ({ data: undefined, status: Status.Error, error: err }));
        });
        return next;
    }, [fn, idleTimeout]);
    const clear = useCallback(() => {
        store.setState(fn, () => ({
            data: undefined,
            status: Status.Idle,
            error: undefined,
            func: undefined,
        }));
    }, [fn]);
    return {
        fn: func,
        isPending: currentData?.status === Status.Pending,
        isFulfilled: currentData?.status === Status.Fulfilled,
        isError: currentData?.status === Status.Error,
        status: currentData?.status,
        data: currentData?.data,
        error: currentData?.error,
        clear,
    };
};
